import { useEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut, RotateCw, Maximize2 } from "lucide-react";

// Lightweight image viewer with zoom / pan / rotate.
// - Buttons and mouse wheel zoom
// - Drag to pan (works with touch + mouse)
// - Double-click / double-tap to toggle fit
// - Rotate 90°
const PhotoViewer = ({ file, blobUrl }) => {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const dragRef = useRef({ active: false, startX: 0, startY: 0, baseX: 0, baseY: 0 });
  const wrapRef = useRef(null);

  const reset = () => { setScale(1); setRotation(0); setTx(0); setTy(0); };
  const zoomIn = () => setScale((s) => Math.min(6, +(s + 0.25).toFixed(2)));
  const zoomOut = () => setScale((s) => {
    const next = Math.max(0.25, +(s - 0.25).toFixed(2));
    if (next <= 1) { setTx(0); setTy(0); }
    return next;
  });
  const rotate = () => setRotation((r) => (r + 90) % 360);

  const onWheel = (e) => {
    if (!wrapRef.current?.contains(e.target)) return;
    e.preventDefault();
    const delta = -e.deltaY * 0.002;
    setScale((s) => {
      const next = Math.min(6, Math.max(0.25, +(s + delta).toFixed(3)));
      if (next <= 1) { setTx(0); setTy(0); }
      return next;
    });
  };

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const startDrag = (clientX, clientY) => {
    if (scale <= 1) return;
    dragRef.current = { active: true, startX: clientX, startY: clientY, baseX: tx, baseY: ty };
  };
  const moveDrag = (clientX, clientY) => {
    if (!dragRef.current.active) return;
    setTx(dragRef.current.baseX + (clientX - dragRef.current.startX));
    setTy(dragRef.current.baseY + (clientY - dragRef.current.startY));
  };
  const endDrag = () => { dragRef.current.active = false; };

  return (
    <div
      className="relative flex h-full flex-col bg-[#0d0f14] text-white"
      data-testid="photo-viewer"
    >
      <div className="flex flex-wrap items-center justify-center gap-2 border-b border-white/10 bg-[#12141a] px-3 py-2">
        <button onClick={zoomOut} data-testid="photo-zoom-out" className="rounded border border-white/15 p-1 hover:bg-white/10">
          <ZoomOut className="h-3.5 w-3.5" />
        </button>
        <span className="w-12 text-center font-mono text-[11px] text-white/70">{Math.round(scale * 100)}%</span>
        <button onClick={zoomIn} data-testid="photo-zoom-in" className="rounded border border-white/15 p-1 hover:bg-white/10">
          <ZoomIn className="h-3.5 w-3.5" />
        </button>
        <span className="mx-1 h-4 w-px bg-white/15" />
        <button onClick={rotate} data-testid="photo-rotate" className="flex items-center gap-1 rounded border border-white/15 px-2 py-1 text-xs hover:bg-white/10">
          <RotateCw className="h-3.5 w-3.5" /> Rotate
        </button>
        <button onClick={reset} data-testid="photo-reset" className="flex items-center gap-1 rounded border border-white/15 px-2 py-1 text-xs hover:bg-white/10">
          <Maximize2 className="h-3.5 w-3.5" /> Fit
        </button>
      </div>
      <div
        ref={wrapRef}
        onMouseDown={(e) => startDrag(e.clientX, e.clientY)}
        onMouseMove={(e) => moveDrag(e.clientX, e.clientY)}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onTouchStart={(e) => { if (e.touches[0]) startDrag(e.touches[0].clientX, e.touches[0].clientY); }}
        onTouchMove={(e) => { if (e.touches[0]) moveDrag(e.touches[0].clientX, e.touches[0].clientY); }}
        onTouchEnd={endDrag}
        onDoubleClick={reset}
        className={`min-h-0 flex-1 select-none overflow-hidden ${scale > 1 ? "cursor-grab active:cursor-grabbing" : ""}`}
      >
        <div className="flex h-full items-center justify-center">
          <img
            src={blobUrl}
            alt={file.name}
            draggable={false}
            data-testid="preview-image"
            style={{ transform: `translate(${tx}px, ${ty}px) scale(${scale}) rotate(${rotation}deg)`, transition: dragRef.current.active ? "none" : "transform 120ms ease-out" }}
            className="max-h-full max-w-full object-contain will-change-transform"
          />
        </div>
      </div>
    </div>
  );
};

export default PhotoViewer;
